import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** The actions a phone may do offline (the backend's `SYNC_COMMAND_TYPES`). */
export type SyncCommandType =
  | 'maintenance.task.startProcedure'
  // Signed on the phone with the person's offline key.
  | 'maintenance.task.start'
  | 'maintenance.task.end'
  // A task's form filled offline, with or without its completion: its answers ride along.
  | 'maintenance.task.completeForm'
  | 'maintenance.task.saveForm'

/** One queued action, as the phone sends it (`SyncCommandDto`). */
export interface SyncCommandBody {
  id: string
  type: SyncCommandType
  tenantId: string
  userId: string
  entityId: string
  expected: { status: 'NOT_STARTED' | 'STARTED' | 'COMPLETED'; assigneeId: string | null }
  payload?: Record<string, unknown>
  /** The phone's clock when the person did it. */
  occurredAt: string
  /** Server clock minus phone clock, as last measured online. */
  clockOffsetMs: number
  /** For a signed action: which offline key, and the signature (base64). */
  signature?: { keyId: string; value: string }
}

/** One of the person's offline signing keys as the server lists it: one per phone, public ids only. */
export interface ServerSigningKey {
  id: string
  deviceId: string
  createdAt: string
}

export type SyncResultStatus = 'applied' | 'duplicate' | 'conflict' | 'rejected'

export interface SyncCommandResult {
  id: string
  status: SyncResultStatus
  code?: string
  message?: string
}

/** Per tenant: whether phones may sign actions with no connection. Off by default. */
export type OfflineSignatureMode = 'OFF' | 'ON'

export interface OfflineProfile {
  serverTime: string
  tenantId: string
  userId: string | null
  offlineWindowHours: number
  /** Whether the tenant lets phones sign actions with no connection. */
  offlineSignatureMode: OfflineSignatureMode
  /** The person's active offline signing keys, so a phone drops a retired one. */
  signingKeyIds: string[]
}

/** A refusal with its HTTP status, so the sync runner can tell "try later" from "never". */
export class SyncHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({ 'Content-Type': 'application/json', 'X-Tenant-Id': tenantId, ...init?.headers }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw new SyncHttpError(message || `Request failed: ${res.status}`, res.status)
  }
  return res.json() as Promise<T>
}

/** A request with no tenant: the signing key belongs to the person, not a tenant. */
async function plain<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({ 'Content-Type': 'application/json', ...init?.headers }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) throw new SyncHttpError(`Request failed: ${res.status}`, res.status)
  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

/**
 * Offline sync for the mobile surface (z8nrz7fh5c). The tenant is passed
 * explicitly, as everywhere on `/m`: a queued action belongs to the tenant it
 * was done in, whatever is selected when it is sent.
 */
export const syncApi = {
  /** The server time (for the phone's clock offset) and the tenant offline window. */
  offlineProfile: (tenantId: string) => request<OfflineProfile>('/sync/offline-profile', tenantId),

  /** Send queued actions, in the order they were done (at most 50). One result per command. */
  sendCommands: (tenantId: string, deviceId: string, commands: SyncCommandBody[]) =>
    request<{ results: SyncCommandResult[] }>('/sync/commands', tenantId, {
      method: 'POST',
      body: JSON.stringify({ deviceId, commands }),
    }),

  /** My active offline signing keys, one per phone. Not tenant-scoped. */
  signingKeys: () => plain<ServerSigningKey[]>('/sync/signing-key'),

  /** Register this phone's new public key. Needs a fresh signing token (the password, proven). */
  registerSigningKey: (key: { deviceId: string; publicKey: string; signingToken: string }) =>
    plain<ServerSigningKey>('/sync/signing-key', { method: 'POST', body: JSON.stringify(key) }),

  /** Signing out: retire my offline signing keys on this phone. Not tenant-scoped. */
  signOutSigningKey: (deviceId: string) =>
    plain<{ retired: number }>('/sync/signing-key/sign-out', { method: 'POST', body: JSON.stringify({ deviceId }) }),

  /** Retire my offline signing keys (a lost phone). Signatures received after this are refused. */
  retireMySigningKey: () => plain<{ retired: number }>('/sync/signing-key', { method: 'DELETE' }),

  /** Retire a user's offline signing key (superadmin). */
  retireSigningKeyFor: (userId: string) =>
    plain<{ retired: number }>(`/sync/signing-key/users/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  /**
   * Sign again, with my password, an offline action refused for its offline
   * signature. The action keeps the time it was done.
   */
  resign: (tenantId: string, id: string, signingToken: string) =>
    request<SyncCommandResult>(`/sync/commands/${encodeURIComponent(id)}/resign`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ signingToken }),
    }),

  /** Acknowledge one of my offline actions that was not applied. */
  dismiss: (tenantId: string, id: string) =>
    request<{ dismissed: boolean }>(`/sync/commands/${encodeURIComponent(id)}/dismiss`, tenantId, {
      method: 'POST',
    }),
}
